import { AddToCompareButton } from '@/components/CompareCart';
import { FindMatchesButton } from '@/components/Matches/FindMatchesButton';
import { Thumbnail } from '@/components/Thumbnail';
import { MatchAsset } from '@/components/Search/SearchTable';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetName } from '@/utils/assets';
import { dateFormat, formatMatchPercentage } from '@/utils/format';
import { Box, Card, CardContent, Chip, styled, Typography } from '@mui/material';
import { getFileExtension } from '@physna/supported-files';
import { Link } from 'react-router-dom';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
}));

const MediaArea = styled(Box)(({ theme }) => ({
  height: 180,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
  position: 'relative',
}));

const MatchPill = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  fontWeight: 600,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

const StyledCardContent = styled(CardContent)({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 12,
  '&:last-child': {
    paddingBottom: 12,
  },
});

const TitleText = styled(Typography)({
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

export const SearchResultCard = ({
  asset,
  onContextMenu,
}: {
  asset: MatchAsset;
  onContextMenu?: (event: React.MouseEvent) => void;
}) => {
  const { getPath } = useTenants();
  const extension = getFileExtension(asset.path);

  return (
    <StyledCard data-id={asset.id} data-field='name' onContextMenu={onContextMenu}>
      <StyledLink to={getPath(`asset/${asset.id}`)}>
        <MediaArea>
          <Thumbnail asset={asset} size={160} noLink />
          {asset.matchPercentage !== undefined && (
            <MatchPill label={`${formatMatchPercentage(asset.matchPercentage)}%`} size='small' />
          )}
        </MediaArea>
        <StyledCardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TitleText variant='body2'>{getAssetName(asset)}</TitleText>
            {extension && <Chip label={extension} size='small' variant='outlined' sx={{ flexShrink: 0 }} />}
          </Box>
          {asset.createdAt && (
            <Typography variant='caption' color='text.secondary'>
              {dateFormat(asset.createdAt)}
            </Typography>
          )}
        </StyledCardContent>
      </StyledLink>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, pb: 1 }}>
        <FindMatchesButton asset={asset} />
        <AddToCompareButton asset={asset} size='small' />
      </Box>
    </StyledCard>
  );
};
